import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { ACCOUNT_MENU_ITEMS, SETTINGS_SUB_ITEMS } from '../config/navConfig';

interface DashboardNavProps {
  orientation?: 'vertical' | 'vertical-sheet';
  onItemClick?: () => void;
}

export const DashboardNav: React.FC<DashboardNavProps> = ({ onItemClick }) => {
  const { user } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const [settingsOpen, setSettingsOpen] = React.useState(location.pathname === '/settings');
  const [pendingInvitations, setPendingInvitations] = useState(0);

  useEffect(() => {
    if (!user?.id || user.role !== 'HELPER') return;
    apiService.interviewIntents.getPendingCount(user.id)
      .then((res: any) => { if (res.success && res.data) setPendingInvitations(res.data); })
      .catch(() => {});
  }, [user]);

  if (!user) return null;

  const visibleItems = ACCOUNT_MENU_ITEMS.filter(
    item =>
      (!item.employerOnly || user.role === 'EMPLOYER') &&
      (!item.helperOnly || user.role === 'HELPER'),
  );

  const activeTab = new URLSearchParams(location.search).get('tab') || 'account';
  const isSettingsActive = location.pathname === '/settings';

  return (
    <nav className="space-y-1">
      {visibleItems.map(({ path, labelKey, icon: Icon }) => {
        const isSettings = path === '/settings';
        const label = t(labelKey);

        if (isSettings) {
          return (
            <div key={path}>
              <button
                type="button"
                onClick={() => setSettingsOpen(prev => !prev)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 rounded-md text-sm font-medium ${isSettingsActive ? 'text-foreground' : 'text-muted-foreground'}`}
              >
                <span className="flex items-center gap-3">
                  <Icon className="h-4 w-4" />
                  {label}
                </span>
                {settingsOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
              {settingsOpen && (
                <div className="ml-7 space-y-1 mt-1">
                  {SETTINGS_SUB_ITEMS.map((sub) => (
                    <Link
                      key={sub.tab}
                      to={`/settings?tab=${sub.tab}`}
                      onClick={onItemClick}
                      className={`block px-3 py-1.5 rounded-md text-sm transition-colors ${
                        activeTab === sub.tab && isSettingsActive
                          ? 'bg-primary text-primary-foreground font-medium'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      {t(sub.labelKey)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        }

        const isActive = location.pathname === path;
        const isInvitations = path === '/helper/invitations';
        return (
          <Link
            key={path}
            to={path}
            onClick={onItemClick}
            className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors w-full ${
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <Icon className="h-4 w-4" />
            <span className="flex-1">{label}</span>
            {isInvitations && pendingInvitations > 0 && (
              <span className="ml-auto flex items-center gap-1">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                </span>
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
                  {pendingInvitations}
                </span>
              </span>
            )}
          </Link>
        );
      })}
      {user.uid && (
        <div className="px-3 pt-2 text-[10px] text-muted-foreground">
          UID: {user.uid}
        </div>
      )}
    </nav>
  );
};

export default DashboardNav;
